import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Search, Plus, Loader2, CalendarPlus, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';

// Import refactored components
import { SummaryCards } from '@/components/pms/SummaryCards';
import { AvailabilityCalendar } from '@/components/pms/AvailabilityCalendar';
import { GuestListDialog } from '@/components/pms/GuestListDialog';
import { CheckInDialog } from '@/components/pms/CheckInDialog';
import { CheckOutDialog } from '@/components/pms/CheckOutDialog';
import { BookingCreationDialog } from '@/components/pms/BookingCreationDialog';
import { FolioViewDialog } from '@/components/pms/FolioViewDialog';
import { ExtendStayDialog } from '@/components/pms/ExtendStayDialog';
import { EarlyCheckOutDialog } from '@/components/pms/EarlyCheckOutDialog';
import { WalkInDialog } from '@/components/pms/WalkInDialog';
import { CloseShiftDialog } from '@/components/pms/CloseShiftDialog';
import { MarinaChat } from '@/components/MarinaChat';
import { useGuestReliability } from '@/hooks/useGuestReliability';
import { getCurrentShiftTime } from '@/utils/timezone';
import { BACKEND_URL } from '@/lib/backendUrl';


export function EnhancedFrontDesk({ hotelId }) {
  // Core data state
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ 
    in_house: { count: 0, guests: [] },
    arrivals_today: { count: 0, guests: [] },
    departures_today: { count: 0, guests: [] }
  });
  
  // Availability calendar state
  const [calendarView, setCalendarView] = useState('week');
  const [availability, setAvailability] = useState(null);
  const [loadingCalendar, setLoadingCalendar] = useState(false);
  
  // Dialog states
  const [activeDialog, setActiveDialog] = useState(null); // 'inhouse' | 'arrivals' | 'departures' | null
  const [checkInDialog, setCheckInDialog] = useState(false);
  const [checkOutDialog, setCheckOutDialog] = useState(false);
  const [folioDialog, setFolioDialog] = useState(false);
  const [extendStayDialog, setExtendStayDialog] = useState(false);
  const [earlyCheckOutDialog, setEarlyCheckOutDialog] = useState(false);
  const [walkInDialog, setWalkInDialog] = useState(false);
  const [selectedGuest, setSelectedGuest] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [createBookingDialog, setCreateBookingDialog] = useState(false);
  
  // Shift tracking state
  const [closeShiftDialog, setCloseShiftDialog] = useState(false);
  const [shiftActivities, setShiftActivities] = useState({
    checkIns: [],
    checkOuts: [],
    walkIns: [],
    bookings: []
  });
  
  // Marina AI state
  const [marinaChatOpen, setMarinaChatOpen] = useState(false);
  
  // Get current user info (safely)
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch {
      return {};
    }
  });
  
  // Determine current shift based on time
  const getCurrentShift = () => {
    const shiftTime = getCurrentShiftTime(); // Uses local timezone
    
    const shifts = {
      morning: {
        name: 'Morning',
        icon: 'https://customer-assets.emergentagent.com/job_easy-stay-ai/artifacts/i6itquyq_Morning%20shift.png',
        gradient: 'from-cyan-400 to-yellow-400',
        shadow: 'shadow-cyan-500/30 hover:shadow-cyan-500/40'
      },
      afternoon: {
        name: 'Afternoon',
        icon: 'https://customer-assets.emergentagent.com/job_easy-stay-ai/artifacts/eg5sawnd_After%20Noon.png',
        gradient: 'from-amber-400 to-orange-500',
        shadow: 'shadow-orange-500/30 hover:shadow-orange-500/40'
      },
      night: {
        name: 'Night',
        icon: 'https://customer-assets.emergentagent.com/job_easy-stay-ai/artifacts/y5eww43y_Night%20shift.png',
        gradient: 'from-indigo-600 to-blue-700',
        shadow: 'shadow-indigo-500/30 hover:shadow-indigo-500/40'
      }
    };
    
    return shifts[shiftTime] || shifts.morning;
  };
  
  const currentShift = getCurrentShift();

  
  // Search state
  const [searchType, setSearchType] = useState('name');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchDateFrom, setSearchDateFrom] = useState('');
  const [searchDateTo, setSearchDateTo] = useState('');
  
  // Create booking dialog - prefilled state
  const [prefilledBookingDate, setPrefilledBookingDate] = useState(null);
  const [prefilledRoomType, setPrefilledRoomType] = useState(null);
  
  // Get reliability data for all guests
  const allGuestEmails = [
    ...summary.in_house.guests.map(g => g.guest_email),
    ...summary.arrivals_today.guests.map(g => g.guest_email),
    ...summary.departures_today.guests.map(g => g.guest_email)
  ];
  const { reliabilityData, loading: reliabilityLoading } = useGuestReliability(allGuestEmails);
  
  useEffect(() => {
    loadSummary();
    loadAvailabilityCalendar();
  }, [hotelId]);
  
  useEffect(() => {
    loadAvailabilityCalendar();
  }, [calendarView]);
  
  const loadSummary = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/pms/${hotelId}/front-desk-summary`);
      const data = await response.json();
      if (data.success) {
        // Add proper IDs to guests
        const enhancedData = {
          ...data,
          in_house: {
            ...data.in_house,
            guests: data.in_house.guests.map(g => ({...g, id: g.booking_id, guest_email: g.guest_email || ''}))
          },
          arrivals_today: {
            ...data.arrivals_today,
            guests: data.arrivals_today.guests.map(g => ({...g, id: g.booking_id, guest_email: g.guest_email || ''}))
          },
          departures_today: {
            ...data.departures_today,
            guests: data.departures_today.guests.map(g => ({...g, id: g.booking_id, guest_email: g.guest_email || ''}))
          }
        };
        setSummary(enhancedData);
      }
    } catch (error) {
      console.error('Error loading summary:', error);
      toast.error('Failed to load front desk summary');
    } finally {
      setLoading(false);
    }
  };
  
  const loadAvailabilityCalendar = async () => {
    try {
      setLoadingCalendar(true);
      const weeks = calendarView === 'week' ? 1 : 4;
      const response = await fetch(`${BACKEND_URL}/api/pms/${hotelId}/availability-calendar?weeks=${weeks}`);
      const data = await response.json();
      if (data.success) {
        setAvailability(data);
      }
    } catch (error) {
      console.error('Error loading availability:', error);
      toast.error('Failed to load availability calendar');
    } finally {
      setLoadingCalendar(false);
    }
  };
  
  const handleCardClick = (cardType) => {
    setActiveDialog(cardType);
  };
  
  const handleCheckIn = async (roomNumber) => {
    if (!selectedGuest) return;
    
    try {
      setProcessing(true);
      const response = await fetch(`${BACKEND_URL}/api/pms/checkin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_id: selectedGuest.id,
          room_number: roomNumber
        })
      });
      
      const data = await response.json();
      if (data.success) {
        // Track check-in activity for shift report
        setShiftActivities(prev => ({
          ...prev,
          checkIns: [...prev.checkIns, {
            guestName: selectedGuest.guest_name,
            roomNumber: roomNumber,
            timestamp: new Date().toISOString()
          }]
        }));
        
        // Don't close dialog immediately - let CheckInDialog handle success state
        loadSummary();
        return; // Let CheckInDialog show success screen
      } else {
        toast.error(data.message || 'Check-in failed');
        throw new Error(data.message || 'Check-in failed');
      }
    } catch (error) {
      console.error('Check-in error:', error);
      toast.error('Failed to check in guest');
      throw error;
    } finally {
      setProcessing(false);
    }
  };
  
  const handleCheckOut = async () => {
    if (!selectedGuest) return;
    
    // Debug logging
    console.log('🔍 CHECKOUT DEBUG - Selected guest:', selectedGuest);
    console.log('🔍 assigned_room_number:', selectedGuest.assigned_room_number);
    console.log('🔍 is_checked_in:', selectedGuest.is_checked_in);
    
    try {
      setProcessing(true);
      // Try multiple possible ID fields
      const bookingId = selectedGuest.booking_id || selectedGuest.id || selectedGuest._id;
      
      console.log('Selected guest for checkout:', selectedGuest);
      console.log('Booking ID:', bookingId);
      
      if (!bookingId) {
        console.error('No booking ID found. Guest object:', selectedGuest);
        toast.error('Booking ID not found. Please contact support.');
        return;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/pms/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_id: bookingId
        })
      });
      
      const data = await response.json();
      console.log('Checkout response:', data);
      
      if (response.ok && data.success) {
        // Track check-out activity for shift report
        setShiftActivities(prev => ({
          ...prev,
          checkOuts: [...prev.checkOuts, {
            guestName: selectedGuest.guest_name,
            roomNumber: selectedGuest.assigned_room_number,
            timestamp: new Date().toISOString()
          }]
        }));
        
        toast.success(`✅ ${selectedGuest.guest_name} checked out successfully!`);
        setCheckOutDialog(false);
        setSelectedGuest(null);
        loadSummary();
        loadAvailabilityCalendar();
      } else {
        const errorMsg = data.message || data.error || data.detail || 'Check-out failed';
        console.error('Checkout failed:', errorMsg, data);
        
        // Show user-friendly error messages
        if (errorMsg.includes('not checked in')) {
          toast.error('Guest must be checked in before checking out. Please check in the guest first.');
        } else {
          toast.error(errorMsg);
        }
      }
    } catch (error) {
      console.error('Check-out error:', error);
      toast.error('Failed to check out guest');
    } finally {
      setProcessing(false);
    }
  };
  
  const openCheckInDialog = (guest) => {
    setSelectedGuest(guest);
    setActiveDialog(null);
    setCheckInDialog(true);
  };
  
  const openCheckOutDialog = (guest) => {
    setSelectedGuest(guest);
    setActiveDialog(null);
    setCheckOutDialog(true);
  };
  
  const handleViewFolio = (guest) => {
    setSelectedGuest(guest);
    setFolioDialog(true);
  };
  
  const handlePrintRegCard = (guest) => {
    // Generate and print registration card for already checked-in guest
    const printWindow = window.open('', '_blank');
    const regCardHTML = generateRegCardHTML(guest);
    printWindow.document.write(regCardHTML);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    toast.success('Registration card sent to printer');
  };
  
  const generateRegCardHTML = (guest) => {
    const today = new Date().toLocaleDateString();
    const roomRate = guest.room_rate || guest.final_price / (guest.nights || 1) || 0;
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Registration Card - ${guest.guest_name}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', system-ui, sans-serif; padding: 30px; background: #f5f5f5; }
          .card-wrapper { max-width: 850px; margin: 0 auto; background: white; box-shadow: 0 10px 40px rgba(0,0,0,0.1); }
          .header { background: linear-gradient(135deg, #0ea5e9 0%, #06b6d4 100%); color: white; padding: 40px; text-align: center; }
          .hotel-logo { width: 80px; height: 80px; margin: 0 auto 15px; background: white; border-radius: 12px; display: flex; align-items: center; justify-content: center; box-shadow: 0 4px 12px rgba(0,0,0,0.15); }
          .hotel-logo-placeholder { font-size: 32px; color: #0ea5e9; font-weight: 700; }
          .hotel-name { font-size: 32px; font-weight: 700; letter-spacing: 1px; margin-bottom: 8px; }
          .card-title { font-size: 14px; font-weight: 500; letter-spacing: 3px; text-transform: uppercase; opacity: 0.95; }
          .content { padding: 40px; }
          .section { margin-bottom: 35px; }
          .section-title { font-size: 12px; font-weight: 700; text-transform: uppercase; color: #0ea5e9; letter-spacing: 1.5px; margin-bottom: 20px; padding-bottom: 8px; border-bottom: 2px solid #e0f2fe; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
          .info-item { padding: 12px 0; border-bottom: 1px solid #e5e7eb; }
          .info-item-full { padding: 12px 0; border-bottom: 1px solid #e5e7eb; grid-column: 1 / -1; }
          .info-label { font-size: 11px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 6px; }
          .info-value { font-size: 16px; font-weight: 600; color: #1f2937; }
          .room-badge { display: inline-block; background: linear-gradient(135deg, #10b981 0%, #059669 100%); color: white; padding: 8px 20px; border-radius: 6px; font-size: 24px; font-weight: 700; letter-spacing: 1px; }
          .rate-badge { display: inline-block; background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); color: white; padding: 6px 16px; border-radius: 6px; font-size: 18px; font-weight: 700; }
          .terms { background: #f9fafb; padding: 20px; border-radius: 8px; margin-top: 30px; border-left: 4px solid #0ea5e9; }
          .terms-title { font-size: 11px; font-weight: 700; color: #374151; margin-bottom: 10px; }
          .terms-text { font-size: 10px; color: #6b7280; line-height: 1.6; }
          .signature-section { margin-top: 50px; padding-top: 30px; border-top: 2px solid #e5e7eb; }
          .sig-label { font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 15px; }
          .sig-line { border-bottom: 2px solid #1f2937; width: 400px; margin: 20px 0; }
          .sig-note { font-size: 11px; color: #9ca3af; font-style: italic; }
          .footer { background: #f9fafb; padding: 25px 40px; text-align: center; border-top: 1px solid #e5e7eb; }
          .footer-text { font-size: 11px; color: #6b7280; line-height: 1.8; }
          @media print { body { background: white; padding: 0; } .card-wrapper { box-shadow: none; } }
        </style>
      </head>
      <body>
        <div class="card-wrapper">
          <div class="header">
            <div class="hotel-logo"><div class="hotel-logo-placeholder">🏨</div></div>
            <div class="hotel-name">The Grand Marina Hotel</div>
            <div class="card-title">Guest Registration Card</div>
          </div>
          <div class="content">
            <div class="section">
              <div class="section-title">Guest Information</div>
              <div class="info-grid">
                <div class="info-item"><div class="info-label">Full Name</div><div class="info-value">${guest.guest_name || ''}</div></div>
                <div class="info-item"><div class="info-label">Email Address</div><div class="info-value">${guest.guest_email || ''}</div></div>
                <div class="info-item"><div class="info-label">Phone Number</div><div class="info-value">${guest.guest_phone || 'Not provided'}</div></div>
                <div class="info-item"><div class="info-label">Registration Date</div><div class="info-value">${today}</div></div>
                <div class="info-item-full"><div class="info-label">Address</div><div class="info-value">${guest.address || guest.guest_address || 'Not provided'}</div></div>
              </div>
            </div>
            <div class="section">
              <div class="section-title">Accommodation Details</div>
              <div class="info-grid">
                <div class="info-item"><div class="info-label">Room Number</div><div class="info-value"><span class="room-badge">${guest.assigned_room_number || guest.room_number || ''}</span></div></div>
                <div class="info-item"><div class="info-label">Room Type</div><div class="info-value">${guest.room_type || ''}</div></div>
                <div class="info-item"><div class="info-label">Check-In Date</div><div class="info-value">${guest.check_in || ''}</div></div>
                <div class="info-item"><div class="info-label">Check-Out Date</div><div class="info-value">${guest.check_out || ''}</div></div>
                <div class="info-item"><div class="info-label">Duration of Stay</div><div class="info-value">${guest.nights || ''} Nights</div></div>
                <div class="info-item"><div class="info-label">Room Rate (Per Night)</div><div class="info-value"><span class="rate-badge">$${roomRate.toFixed(2)}</span></div></div>
              </div>
            </div>
            <div class="terms">
              <div class="terms-title">Terms & Conditions</div>
              <div class="terms-text">By signing below, I acknowledge that I have read and agree to the hotel's terms and conditions. I understand that I am responsible for all charges incurred during my stay, including room charges, taxes, and any additional services or amenities used.</div>
            </div>
            <div class="signature-section">
              <div class="sig-label">Guest Signature</div>
              <div class="sig-line"></div>
              <div class="sig-note">I agree to the terms and conditions of my stay</div>
            </div>
          </div>
          <div class="footer">
            <div class="footer-text"><strong>The Grand Marina Hotel</strong><br>Dubai, UAE | contact@grandmarina.com | +971 4 XXX XXXX<br>Thank you for choosing The Grand Marina Hotel. We wish you a pleasant stay!</div>
          </div>
        </div>
      </body>
      </html>
    `;
  };
  
  const openExtendStayDialog = (guest) => {
    setSelectedGuest(guest);
    setActiveDialog(null);
    setExtendStayDialog(true);
  };
  
  const openEarlyCheckOutDialog = (guest) => {
    setSelectedGuest(guest);
    setActiveDialog(null);
    setEarlyCheckOutDialog(true);
  };
  
  const handleExtendStay = async (newCheckOutDate) => {
    if (!selectedGuest) return;
    
    try {
      setProcessing(true);
      const response = await fetch(`${BACKEND_URL}/api/pms/booking/${selectedGuest.id}/edit-dates`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          new_check_out: newCheckOutDate
        })
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success(`✅ Stay extended! Guest moved to In-House list.`);
        setExtendStayDialog(false);
        setSelectedGuest(null);
        loadSummary();
        loadAvailabilityCalendar();
      } else {
        toast.error(data.message || data.error || 'Failed to extend stay');
      }
    } catch (error) {
      console.error('Extend stay error:', error);
      toast.error('Failed to extend stay');
    } finally {
      setProcessing(false);
    }
  };
  
  const handleEarlyCheckOut = async (newCheckOutDate) => {
    if (!selectedGuest) return;
    
    try {
      setProcessing(true);
      const response = await fetch(`${BACKEND_URL}/api/pms/booking/${selectedGuest.id}/edit-dates`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          new_check_out: newCheckOutDate
        })
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success(`✅ Early checkout scheduled! Guest moved to Departures.`);
        setEarlyCheckOutDialog(false);
        setSelectedGuest(null);
        loadSummary();
        loadAvailabilityCalendar();
      } else {
        toast.error(data.message || data.error || 'Failed to schedule early checkout');
      }
    } catch (error) {
      console.error('Early checkout error:', error);
      toast.error('Failed to schedule early checkout');
    } finally {
      setProcessing(false);
    }
  };
  
  const handleCalendarCellClick = (date, roomTypeId, roomTypeName) => {
    // Pre-fill booking form with selected date and room type
    setPrefilledBookingDate(date);
    setPrefilledRoomType(roomTypeId);
    setCreateBookingDialog(true);
  };
  
  const handleBookingCreated = () => {
    // Track booking creation for shift report
    setShiftActivities(prev => ({
      ...prev,
      bookings: [...prev.bookings, {
        timestamp: new Date().toISOString()
      }]
    }));
    
    // Refresh data after booking is created
    loadSummary();
    loadAvailabilityCalendar();
  };

  // Marina AI Command Handler
  const handleMarinaCommand = async (action, data) => {
    console.log('Marina command:', action, data);
    
    try {
      switch (action) {
        case 'check_in':
          // Search for guest by name
          if (data.guest_name) {
            setSearchQuery(data.guest_name);
            setSearchType('name');
            // Trigger search
            const response = await fetch(
              `${BACKEND_URL}/api/pms/${hotelId}/guest-search?query=${encodeURIComponent(data.guest_name)}&search_type=name`
            );
            const result = await response.json();
            if (result.success && result.results.length > 0) {
              setSelectedGuest(result.results[0]);
              setCheckInDialog(true);
            } else {
              toast.error(`Guest "${data.guest_name}" not found`);
            }
          }
          break;
          
        case 'check_out':
          // Search for guest by room or name
          if (data.room_number) {
            setSearchQuery(data.room_number);
            setSearchType('room');
            const response = await fetch(
              `${BACKEND_URL}/api/pms/${hotelId}/guest-search?query=${encodeURIComponent(data.room_number)}&search_type=room`
            );
            const result = await response.json();
            if (result.success && result.results.length > 0) {
              setSelectedGuest(result.results[0]);
              setCheckOutDialog(true);
            } else {
              toast.error(`No guest found in room ${data.room_number}`);
            }
          } else if (data.guest_name) {
            setSearchQuery(data.guest_name);
            setSearchType('name');
            const response = await fetch(
              `${BACKEND_URL}/api/pms/${hotelId}/guest-search?query=${encodeURIComponent(data.guest_name)}&search_type=name`
            );
            const result = await response.json();
            if (result.success && result.results.length > 0) {
              setSelectedGuest(result.results[0]);
              setCheckOutDialog(true);
            } else {
              toast.error(`Guest "${data.guest_name}" not found`);
            }
          }
          break;
          
        case 'search_guest':
          // Execute search
          if (data.query) {
            setSearchQuery(data.query);
            setSearchType('name');
            const response = await fetch(
              `${BACKEND_URL}/api/pms/${hotelId}/guest-search?query=${encodeURIComponent(data.query)}&search_type=name`
            );
            const result = await response.json();
            if (result.success) {
              setSearchResults(result.results || []);
              if (result.results.length > 0) {
                toast.success(`Found ${result.results.length} guest(s)`);
              } else {
                toast.info('No guests found');
              }
            }
          }
          break;
          
        case 'show_arrivals':
          setActiveDialog('arrivals');
          break;
          
        case 'show_departures':
          setActiveDialog('departures');
          break;
          
        case 'show_inhouse':
          setActiveDialog('in_house');
          break;
          
        case 'show_available_rooms':
          // Could open a room availability dialog or show calendar
          toast.info('Scroll down to view the Availability Calendar');
          break;
          
        case 'show_occupancy':
          // Show occupancy info in a toast
          const occupancyRate = summary.in_house?.count 
            ? Math.round((summary.in_house.count / 30) * 100) 
            : 0;
          toast.success(
            `Current Occupancy: ${summary.in_house?.count || 0} guests (${occupancyRate}%)`,
            { duration: 5000 }
          );
          break;
          
        case 'close_shift':
          setCloseShiftDialog(true);
          break;
          
        case 'create_booking':
          setCreateBookingDialog(true);
          break;
          
        case 'walk_in':
          setWalkInDialog(true);
          break;
          
        default:
          console.log('Unknown command:', action);
      }
    } catch (error) {
      console.error('Error handling Marina command:', error);
      toast.error('Failed to execute command');
    }
  };

  
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a search query');
      return;
    }
    
    try {
      setSearching(true);
      const response = await fetch(
        `${BACKEND_URL}/api/pms/${hotelId}/guest-search?query=${encodeURIComponent(searchQuery)}&search_type=${searchType}`
      );
      const data = await response.json();
      
      if (data.success) {
        setSearchResults(data.results || []);
        if (data.results.length === 0) {
          toast.info('No guests found matching your search');
        } else {
          toast.success(`Found ${data.results.length} guest${data.results.length > 1 ? 's' : ''}`);
        }
      } else {
        toast.error('Search failed');
      }
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Failed to search guests');
    } finally {
      setSearching(false);
    }
  };
  
  const getActiveGuestList = () => {
    switch (activeDialog) {
      case 'in_house':
        return summary.in_house.guests;
      case 'arrivals':
        return summary.arrivals_today.guests;
      case 'departures':
        return summary.departures_today.guests;
      default:
        return [];
    }
  };
  
  const getActiveDialogTitle = () => {
    switch (activeDialog) {
      case 'in_house':
        return 'In-House Guests';
      case 'arrivals':
        return 'Arrivals Today';
      case 'departures':
        return 'Departures Today';
      default:
        return '';
    }
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <TooltipProvider delayDuration={200}>
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/20" data-testid="enhanced-front-desk">
      {/* Main Content Container */}
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Quick Action Bar - Icon-First Design */}
        <div className="flex items-center justify-end gap-3">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={() => setWalkInDialog(true)}
                className="h-14 w-14 bg-white/80 hover:bg-white border-2 border-gray-200 hover:border-emerald-500 shadow-md hover:shadow-lg transition-all duration-300 relative overflow-hidden p-2"
                size="icon"
                data-testid="walk-in-btn"
                aria-label="Walk-In Guest"
              >
                <div className="w-full h-full overflow-hidden flex items-center justify-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_easy-stay-ai/artifacts/5cfcvww2_walk%20in.png" 
                    alt="Walk-In Guest"
                    className="w-full h-full object-contain"
                    style={{ transform: 'scale(1.6)' }}
                  />
                </div>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p className="font-semibold">Walk-In Guest</p>
              <p className="text-xs text-gray-400">Quick check-in for guests without reservation</p>
            </TooltipContent>
          </Tooltip>
          
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={() => setCreateBookingDialog(true)}
                data-testid="create-booking-btn"
                className="h-14 w-14 bg-white/80 hover:bg-white border-2 border-gray-200 hover:border-blue-500 shadow-md hover:shadow-lg transition-all duration-300 relative overflow-hidden p-2"
                size="icon"
                aria-label="Create Booking"
              >
                <div className="w-full h-full overflow-hidden flex items-center justify-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_easy-stay-ai/artifacts/07w8n8mo_Booking%20icon.png" 
                    alt="Create Booking"
                    className="w-full h-full object-contain"
                    style={{ transform: 'scale(1.6)' }}
                  />
                </div>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p className="font-semibold">Create Booking</p>
              <p className="text-xs text-gray-400">New reservation for future guest</p>
            </TooltipContent>
          </Tooltip>
          
          <Separator orientation="vertical" className="h-12" />
          
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={() => setCloseShiftDialog(true)}
                data-testid="close-shift-btn"
                className="h-14 w-14 bg-white/80 hover:bg-white border-2 border-gray-200 hover:border-orange-500 shadow-md hover:shadow-lg transition-all duration-300 relative overflow-hidden p-1"
                size="icon"
                aria-label={`Close ${currentShift.name} Shift`}
              >
                <div className="w-full h-full overflow-hidden flex items-center justify-center">
                  <img 
                    src={currentShift.icon} 
                    alt={`${currentShift.name} Shift`}
                    className="w-full h-full object-contain"
                    style={{ transform: 'scale(1.6)' }}
                  />
                </div>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p className="font-semibold">Close {currentShift.name} Shift</p>
              <p className="text-xs text-gray-400">End your {currentShift.name.toLowerCase()} shift and generate report</p>
            </TooltipContent>
          </Tooltip>
        </div>
        
        {/* Guest Search */}
        <Card className="border border-gray-200/50 shadow-lg shadow-black/5 bg-white/80 backdrop-blur-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="w-5 h-5" />
            Guest Search
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex gap-4">
              <Select value={searchType} onValueChange={setSearchType}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name">Name</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="phone">Phone</SelectItem>
                  <SelectItem value="room">Room Number</SelectItem>
                </SelectContent>
              </Select>
              
              <Input
                placeholder={`Search by ${searchType}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1"
                data-testid="search-input"
              />
              
              <Button onClick={handleSearch} disabled={searching} data-testid="search-btn">
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
              </Button>
            </div>
            
            {/* Date Range Filter */}
            <div className="flex gap-4 items-end">
              <div className="space-y-1 flex-1">
                <Label htmlFor="search-date-from" className="text-xs">Filter by Check-in Date From</Label>
                <Input
                  id="search-date-from"
                  type="date"
                  value={searchDateFrom}
                  onChange={(e) => setSearchDateFrom(e.target.value)}
                  data-testid="search-date-from"
                />
              </div>
              <div className="space-y-1 flex-1">
                <Label htmlFor="search-date-to" className="text-xs">To</Label>
                <Input
                  id="search-date-to"
                  type="date"
                  value={searchDateTo}
                  onChange={(e) => setSearchDateTo(e.target.value)}
                  min={searchDateFrom}
                  data-testid="search-date-to"
                />
              </div>
              {(searchDateFrom || searchDateTo) && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => {
                    setSearchDateFrom('');
                    setSearchDateTo('');
                  }}
                >
                  Clear Dates
                </Button>
              )}
            </div>
            
            {searchResults.length > 0 && (
              <div className="mt-4 space-y-3 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg relative">
                {/* Close X Button - Prominent */}
                <button
                  onClick={() => {
                    setSearchResults([]);
                    setSearchQuery('');
                  }}
                  className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 shadow-lg transition-all hover:scale-110 z-10"
                  aria-label="Close search results"
                  data-testid="close-search-results"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
                
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-blue-900">
                    🔍 {searchResults.length} result(s) found
                  </p>
                  <span className="text-xs text-blue-600">Click X to close results</span>
                </div>
                
                {/* Search Results List */}
                <div className="space-y-3 max-h-[500px] overflow-y-auto border-t pt-3">
                  {searchResults.map((guest) => (
                    <Card 
                      key={guest.id || guest.booking_id} 
                      className="hover:shadow-md transition-all duration-200 border-l-4 border-l-blue-500"
                      data-testid={`search-result-${guest.id || guest.booking_id}`}
                    >
                      <div className="p-4">
                        {/* Guest Header */}
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-start gap-3">
                            {/* Avatar */}
                            <div className="bg-gradient-to-br from-blue-500 to-cyan-500 text-white w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg shadow-md">
                              {guest.guest_name?.charAt(0).toUpperCase() || 'G'}
                            </div>
                            
                            {/* Guest Info */}
                            <div>
                              <h4 className="font-bold text-gray-900 text-base">
                                {guest.guest_name}
                              </h4>
                              <div className="flex flex-wrap gap-3 text-xs text-gray-600 mt-1">
                                {guest.guest_email && (
                                  <span className="flex items-center gap-1">
                                    <span className="text-blue-500">✉</span> {guest.guest_email}
                                  </span>
                                )}
                                {guest.guest_phone && (
                                  <span className="flex items-center gap-1">
                                    <span className="text-green-500">📞</span> {guest.guest_phone}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Status Badge */}
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            guest.status === 'checked_in' 
                              ? 'bg-green-100 text-green-700' 
                              : guest.status === 'confirmed'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {guest.status === 'checked_in' ? 'Checked In' : 
                             guest.status === 'confirmed' ? 'Confirmed' : 
                             guest.status || 'N/A'}
                          </span>
                        </div>
                        
                        {/* Booking Details */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
                          <div>
                            <span className="text-gray-500 text-xs">Room</span>
                            <p className="font-semibold text-gray-900">
                              {guest.assigned_room_number || 'TBD'}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-500 text-xs">Check-In</span>
                            <p className="font-semibold text-gray-900">
                              {guest.check_in ? new Date(guest.check_in).toLocaleDateString() : 'N/A'}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-500 text-xs">Check-Out</span>
                            <p className="font-semibold text-gray-900">
                              {guest.check_out ? new Date(guest.check_out).toLocaleDateString() : 'N/A'}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-500 text-xs">Booking ID</span>
                            <p className="font-mono text-xs text-gray-600">
                              {(guest.booking_id || guest.id || '').substring(0, 8)}...
                            </p>
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex gap-2 pt-3 border-t">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setSelectedGuest(guest);
                              setFolioDialog(true);
                            }}
                            className="flex-1"
                          >
                            View Folio
                          </Button>
                          
                          {guest.status === 'confirmed' && (
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedGuest(guest);
                                setCheckInDialog(true);
                              }}
                              className="flex-1 bg-green-600 hover:bg-green-700"
                            >
                              Check In
                            </Button>
                          )}
                          
                          {guest.status === 'checked_in' && (
                            <Button
                              size="sm"
                              onClick={() => {
                                setSelectedGuest(guest);
                                setCheckOutDialog(true);
                              }}
                              className="flex-1 bg-orange-600 hover:bg-orange-700"
                            >
                              Check Out
                            </Button>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
      
      <Separator />
      
      {/* Summary Cards - MOVED BELOW SEARCH */}
      <SummaryCards 
        summary={summary} 
        onCardClick={handleCardClick}
      />
      
      {/* Availability Calendar */}
      <AvailabilityCalendar
        availability={availability}
        loading={loadingCalendar}
        view={calendarView}
        onViewChange={setCalendarView}
        onCellClick={handleCalendarCellClick}
      />
      
      {/* Guest List Dialog */}
      <GuestListDialog
        open={activeDialog !== null}
        onOpenChange={(open) => !open && setActiveDialog(null)}
        title={getActiveDialogTitle()}
        guests={getActiveGuestList()}
        reliabilityData={reliabilityData}
        onCheckIn={openCheckInDialog}
        onCheckOut={openCheckOutDialog}
        onViewFolio={handleViewFolio}
        onExtendStay={openExtendStayDialog}
        onEarlyCheckOut={openEarlyCheckOutDialog}
        onPrintRegCard={handlePrintRegCard}
        showCheckInButton={activeDialog === 'arrivals'}
        showCheckOutButton={activeDialog === 'departures'}
        showExtendStayButton={activeDialog === 'departures'}
        showEarlyCheckOutButton={activeDialog === 'in_house'}
        showPrintRegCardButton={activeDialog === 'in_house'}
      />
      
      {/* Check-In Dialog */}
      <CheckInDialog
        open={checkInDialog}
        onOpenChange={setCheckInDialog}
        guest={selectedGuest}
        onConfirm={handleCheckIn}
        loading={processing}
        hotelId={hotelId}
      />
      
      {/* Check-Out Dialog */}
      <CheckOutDialog
        open={checkOutDialog}
        onOpenChange={setCheckOutDialog}
        guest={selectedGuest}
        onConfirm={handleCheckOut}
        loading={processing}
        hotelId={hotelId}
      />
      
      {/* Folio View Dialog */}
      <FolioViewDialog
        open={folioDialog}
        onOpenChange={setFolioDialog}
        guest={selectedGuest}
        hotelId={hotelId}
      />
      
      {/* Extend Stay Dialog */}
      <ExtendStayDialog
        open={extendStayDialog}
        onOpenChange={setExtendStayDialog}
        guest={selectedGuest}
        onConfirm={handleExtendStay}
        loading={processing}
      />
      
      {/* Early Check-Out Dialog */}
      <EarlyCheckOutDialog
        open={earlyCheckOutDialog}
        onOpenChange={setEarlyCheckOutDialog}
        guest={selectedGuest}
        onConfirm={handleEarlyCheckOut}
        loading={processing}
      />
      
      {/* Walk-In Dialog */}
      <WalkInDialog
        open={walkInDialog}
        onOpenChange={setWalkInDialog}
        hotelId={hotelId}
        onSuccess={(walkInData) => {
          // Track walk-in activity for shift report
          if (walkInData) {
            setShiftActivities(prev => ({
              ...prev,
              walkIns: [...prev.walkIns, walkInData]
            }));
          }
          
          setWalkInDialog(false);
          loadSummary();
          loadAvailabilityCalendar();
        }}
      />
      
      {/* Create Booking Dialog */}
      <BookingCreationDialog
        open={createBookingDialog}
        onOpenChange={setCreateBookingDialog}
        hotelId={hotelId}
        prefilledDate={prefilledBookingDate}
        prefilledRoomType={prefilledRoomType}
        onBookingCreated={handleBookingCreated}
      />
      
      {/* Close Shift Dialog */}
      <CloseShiftDialog
        open={closeShiftDialog}
        onOpenChange={setCloseShiftDialog}
        hotelId={hotelId}
        staffEmail={currentUser.email}
        staffName={currentUser.name || currentUser.email}
        shiftActivities={shiftActivities}
      />
      
      {/* Marina AI Chat Assistant */}
      <MarinaChat
        hotelId={hotelId}
        currentUser={currentUser}
        isOpen={marinaChatOpen}
        onToggle={() => setMarinaChatOpen(!marinaChatOpen)}
        onCommand={handleMarinaCommand}
      />
      </div>
    </div>
    </TooltipProvider>
  );
}
